import { Component, type ReactNode } from 'react'
import {
  Coffee,
  Home,
  LifeBuoy,
  RefreshCcw,
  Sparkles,
} from 'lucide-react'
import { reportError } from '@/shared/lib/error-reporter'
import { mediaUrl } from '@/shared/lib/media'
import { asset } from '@/shared/lib/asset'

interface Props {
  /** A short slug used in logs to identify which route raised. */
  routeId: string
  children: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  /**
   * True when the error looks like a chunk-loading failure (post-
   * deploy, the user is holding stale asset URLs). We surface a calmer
   * reassuring message and do an auto-reload after a short pause.
   */
  isChunkError: boolean
  /** Auto-reload countdown shown for chunk errors. */
  countdown: number
}

/**
 * Per-route boundary. Sits between `Suspense` and the route component
 * so a render error in /prompts doesn't take down /courses or the
 * navbar. Reports the error to the configured reporter (Sentry today,
 * a no-op in development) before showing a localised retry surface.
 *
 * Friendly UX:
 *  • Detects the most common production error — a chunk-loading
 *    failure caused by a fresh deploy invalidating the cached asset
 *    URL — and reloads automatically with a 5-second countdown.
 *  • Hides the raw error message from visitors (it leaks file paths
 *    that look scary). Admins / dev mode still see it.
 *  • Offers two CTAs: reload + go home.
 */
export class RouteErrorBoundary extends Component<Props, State> {
  state: State = {
    hasError: false,
    error: null,
    isChunkError: false,
    countdown: 5,
  }

  /** setInterval id for the chunk-error countdown. */
  private timer: number | null = null

  static getDerivedStateFromError(error: Error): State {
    const msg = (error?.message ?? '').toLowerCase()
    const isChunkError =
      msg.includes('chunk') ||
      msg.includes('failed to fetch dynamically imported module') ||
      msg.includes('importing a module script failed') ||
      msg.includes('loading css chunk') ||
      msg.includes('loading chunk')
    return { hasError: true, error, isChunkError, countdown: 5 }
  }

  componentDidCatch(error: Error, info: { componentStack?: string }) {
    reportError(error, {
      route: this.props.routeId,
      componentStack: info.componentStack,
    })
    if (this.state.isChunkError) {
      // Tick down once per second; reload at 0.
      this.timer = window.setInterval(() => {
        this.setState((prev) => {
          if (prev.countdown <= 1) {
            window.clearInterval(this.timer!)
            window.location.reload()
            return prev
          }
          return { ...prev, countdown: prev.countdown - 1 }
        })
      }, 1000)
    }
  }

  componentWillUnmount() {
    if (this.timer) window.clearInterval(this.timer)
  }

  reset = () => {
    if (this.timer) window.clearInterval(this.timer)
    if (this.state.isChunkError) {
      window.location.reload()
      return
    }
    this.setState({ hasError: false, error: null, isChunkError: false })
  }

  render() {
    if (!this.state.hasError) return this.props.children

    const { isChunkError, countdown } = this.state
    const isDev = import.meta.env.DEV

    return (
      <div className="relative isolate flex min-h-[70dvh] items-center justify-center overflow-hidden px-4 py-12 sm:py-20">
        {/* Decorative aurora */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-10"
        >
          <div className="absolute left-1/2 top-1/4 h-[420px] w-[420px] -translate-x-1/2 rounded-full bg-gradient-to-br from-primary/30 via-primary/10 to-transparent opacity-40 blur-3xl" />
          <div className="absolute inset-0 grid-bg opacity-25" />
        </div>

        <div className="mx-auto w-full max-w-xl text-center">
          {/* Circular media core with orbiting glow rings */}
          <div className="relative mx-auto h-44 w-44 sm:h-56 sm:w-56">
            <span
              aria-hidden
              className="absolute inset-0 animate-pulse-glow rounded-full bg-primary/20 blur-2xl"
            />
            <span
              aria-hidden
              className="pointer-events-none absolute inset-0 rounded-full border border-primary/20"
            />
            <span
              aria-hidden
              className="pointer-events-none absolute inset-3 rounded-full border border-primary/10"
            />
            {/*
              Three layers, top to bottom:
                1. Looping video (preferred when present in /public)
                2. Static illustration (fallback if no video)
                3. Lucide icon plate (final fallback if neither asset exists)
              Each layer hides itself on error so the layer below shows.
            */}
            <div className="absolute inset-6 overflow-hidden rounded-full border-2 border-primary/30 shadow-[0_0_50px_-10px_hsl(var(--primary)/0.5)]">
              <video
                src={mediaUrl('loops/error-loop.mp4')}
                poster={asset('/error-illustration.webp')}
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
                aria-hidden
                className="relative h-full w-full object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = 'none'
                }}
              >
                <track kind="captions" />
              </video>
              <img
                src={asset('/error-illustration.webp')}
                alt=""
                aria-hidden
                className="absolute inset-0 -z-[1] h-full w-full object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = 'none'
                }}
              />
            </div>
            <span
              aria-hidden
              className="absolute inset-6 -z-10 grid place-items-center rounded-full bg-gradient-to-br from-primary/15 to-primary/8 text-primary ring-1 ring-primary/30"
            >
              {isChunkError ? (
                <Sparkles className="h-10 w-10" />
              ) : (
                <Coffee className="h-10 w-10" />
              )}
            </span>
            <span
              aria-hidden
              className="pointer-events-none absolute -bottom-2 left-1/2 h-1 w-28 -translate-x-1/2 rounded-full bg-primary/50 blur-sm"
            />
          </div>

          {/* Eyebrow pill */}
          <span
            className="mt-6 inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 font-headline text-[10px] uppercase tracking-[0.18em] text-primary"
            lang="en"
          >
            {isChunkError ? 'Fresh update' : 'Hiccup'}
          </span>

          {/* Copy */}
          <h1
            className="mt-4 font-display text-2xl font-extrabold tracking-tight sm:text-3xl"
            lang="ar"
          >
            {isChunkError ? (
              <>تحديث جديد للموقع 🎉</>
            ) : (
              <>طلعت قهوة سريعة ☕</>
            )}
          </h1>
          <p
            className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-muted-foreground sm:text-base"
            lang="ar"
          >
            {isChunkError ? (
              <>
                الموقع نزّل نسخة أحدث وأنت في الصفحة. هنعيد التحميل تلقائياً
                خلال{' '}
                <span className="font-headline tabular-nums text-foreground">
                  {countdown}
                </span>{' '}
                ثانية.
              </>
            ) : (
              <>
                حاجة صغيرة بايظة في الصفحة دي. خد نَفَس، وجرّب تاني. لو فضلت،
                ابعتلنا برسالة سريعة.
              </>
            )}
          </p>
          <p
            className="mx-auto mt-1.5 max-w-md text-xs text-muted-foreground/80"
            lang="en"
          >
            {isChunkError
              ? 'A fresh build just shipped. Reloading shortly…'
              : 'Tiny glitch on this page. Refresh — or tell us if it sticks.'}
          </p>

          {/* CTAs */}
          <div className="mt-7 flex flex-col items-center justify-center gap-2 sm:flex-row">
            <button
              type="button"
              onClick={this.reset}
              className="inline-flex h-12 items-center gap-2 rounded-xl bg-primary px-6 text-sm font-semibold text-primary-foreground shadow-glow-sm transition-all hover:bg-primary/90 hover:shadow-glow"
            >
              <RefreshCcw
                className={`h-4 w-4 ${isChunkError ? 'animate-spin' : ''}`}
                aria-hidden
              />
              <span lang="ar">
                {isChunkError ? 'تحديث الآن' : 'حاول مرة تانية'}
              </span>
            </button>
            <a
              href="/"
              className="inline-flex h-12 items-center gap-2 rounded-xl border border-border bg-card/60 px-6 text-sm font-medium text-foreground/90 hover:border-primary/40 hover:text-foreground"
            >
              <Home className="h-4 w-4" aria-hidden />
              <span lang="ar">الرئيسية</span>
            </a>
          </div>

          {/* Subtle support hint */}
          {!isChunkError && (
            <p className="mt-6 inline-flex items-center gap-1.5 text-xs text-muted-foreground/70">
              <LifeBuoy className="h-3.5 w-3.5" aria-hidden />
              <a
                href="mailto:hello@abdallah-issa.com"
                className="hover:text-primary hover:underline"
                dir="ltr"
              >
                hello@abdallah-issa.com
              </a>
            </p>
          )}

          {/* Dev-only error box. Hidden from end users entirely.
              Errors are silently logged to /api/errors → admin panel. */}
          {isDev && this.state.error?.message && (
            <details className="mx-auto mt-8 max-w-lg text-left">
              <summary className="cursor-pointer text-[11px] font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground">
                Dev details
              </summary>
              <pre
                dir="ltr"
                className="mt-2 max-h-48 overflow-auto rounded-xl border border-border bg-card p-3 text-start text-[11px] leading-relaxed text-muted-foreground"
              >
                {this.state.error.message}
              </pre>
            </details>
          )}
        </div>
      </div>
    )
  }
}
